import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { LabOrder } from '../../store/labOrdersSlice'

interface OrderInfoProps {
  order: LabOrder
  onUpdateStatus: (newStatus: string) => Promise<void>
}

const STATUS_PROGRESSION: Record<string, string[]> = {
  'Ordered': ['Sample Collected', 'Sent to Lab', 'Results Received'],
  'Sample Collected': ['Sent to Lab', 'Results Received'],
  'Sent to Lab': ['Results Received'],
  'Results Received': [],
  'Reviewed': [],
}

export function OrderInfo({ order, onUpdateStatus }: OrderInfoProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [updating, setUpdating] = useState(false)

  const nextStatuses = STATUS_PROGRESSION[order.status] || []

  const handleStatusChange = async (newStatus: string) => {
    setDropdownOpen(false)
    setUpdating(true)
    await onUpdateStatus(newStatus)
    setUpdating(false)
  }

  return (
    <div data-testid="order-info-section" className="bg-surface border border-border rounded-[6px]">
      <div className="flex items-center justify-between border-b border-border-light px-4 py-2.5">
        <h2 className="text-[14px] font-medium text-text-primary">Order Info</h2>
      </div>

      <div className="px-4 py-3 flex flex-col gap-3">
        <div className="flex gap-6 flex-wrap">
          <div>
            <span className="text-[11px] text-text-muted block mb-0.5">Test Name</span>
            <span className="text-[13px] text-text-primary" data-testid="order-info-test-name">{order.test_name}</span>
          </div>
          <div>
            <span className="text-[11px] text-text-muted block mb-0.5">Priority</span>
            <span className="text-[13px] text-text-primary" data-testid="order-info-priority">{order.priority}</span>
          </div>
        </div>

        <div>
          <span className="text-[11px] text-text-muted block mb-0.5">Instructions / Notes</span>
          <p className="text-[13px] text-text-primary whitespace-pre-wrap" data-testid="order-info-notes">
            {order.notes || <span className="text-text-disabled italic">No instructions provided</span>}
          </p>
        </div>

        {/* Update Status */}
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-text-muted">Update Status</span>
          <div className="relative">
            <button
              data-testid="update-status-dropdown"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              disabled={nextStatuses.length === 0 || updating}
              className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer disabled:opacity-50 disabled:cursor-default"
            >
              {order.status}
              {nextStatuses.length > 0 && <ChevronDown size={12} className="text-text-muted" />}
            </button>
            {dropdownOpen && nextStatuses.length > 0 && (
              <div
                data-testid="update-status-options"
                className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[180px]"
                style={{ boxShadow: 'var(--shadow-elevation-1)' }}
              >
                {nextStatuses.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`status-option-${s.toLowerCase().replace(/ /g, '-')}`}
                    onClick={() => handleStatusChange(s)}
                    className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
